import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'src/utils/store'
import { useDispatch } from 'react-redux'
import { actions } from '../../TransfersState'
import { capitalizeString } from 'src/utils/capitalize'
import { DeleteOutline, Info, OpenInNew } from '@mui/icons-material'
import { useHistory } from 'react-router-dom'
import * as S from './TransferPagesManagementTable.styles'
import TableMui from 'src/components/TableMui'
import {
  TableErrorsLog, PageStatus, PageStatusFilterIds, PageStatusFilters,
} from '../../TransfersTypes'
import { toast } from 'react-toastify'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import { getEnumKeyByValue } from 'src/utils/enum'
import { Box, Tooltip, Typography } from '@mui/material'

interface TransferPagesManagementTableProps {
  idList: number
}

const TransferPagesManagementTable = ({
  idList,
}: TransferPagesManagementTableProps) => {
  const { transferListPages } = useSelector((state) => state.transfers)

  const [errorsLog, setErrorsLog] = useState<TableErrorsLog[]>([])

  const dispatch = useDispatch()
  const history = useHistory()

  const [selectedListPages, setSelectedListPages] = useState<number[]>([])

  const [modalRemoveTransferListPage, setModalRemoveTransferListPage] = useState({
    show: false,
    idPage: -1,
  })
  const [showModalIssueInvoices, setShowModalIssueInvoices] = useState(false)
  const [preventDoubleClick, setPreventDoubleClick] = useState(false)

  const [statusPages, setStatusPages] = useState<number>(PageStatusFilterIds.ALL_STATUS)
  const filterStatusPagesOptions = Object.values(PageStatusFilters).map((value) => ({
    description: value,
    id: PageStatusFilterIds[getEnumKeyByValue(PageStatusFilters, value)],
  }))

  const getTransferListPages = () => dispatch(actions.getTransferListPages(idList))

  useEffect(() => {
    if (idList) {
      getTransferListPages()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idList])

  useEffect(() => {
    setSelectedListPages([])
  }, [statusPages])

  const handleOpenPage = (idPage: number) => {
    history.push(`/transfers/manage/page/${idPage}/details`)
  }

  const handleRemovePage = (idPage?: number) => {
    if (preventDoubleClick) {
      return
    }
    if (!modalRemoveTransferListPage.show && idList) {
      setModalRemoveTransferListPage({
        show: true,
        idPage: idPage!,
      })
    } else if (modalRemoveTransferListPage.show && modalRemoveTransferListPage.idPage !== -1) {
      setPreventDoubleClick(true)

      const onError = () => {
        setPreventDoubleClick(false)
      }

      const onSuccess = () => {
        setPreventDoubleClick(false)
        setSelectedListPages([])
        setModalRemoveTransferListPage({ show: false, idPage: -1 })
        toast.success('Página de transferência excluída ou cancelada com sucesso!')
        getTransferListPages()
      }

      dispatch(actions.removeTransferListPage({
        idPage: modalRemoveTransferListPage.idPage,
        onError,
        onSuccess,
      }))
    }
  }

  const pagesFilteredByStatus = transferListPages.filter((page) => {
    switch (statusPages) {
      case PageStatusFilterIds.ALL_STATUS:
        return true

      case PageStatusFilterIds.OPENED:
        return [
          PageStatus.CREATED.toString(),
          PageStatus.STARTED.toString(),
        ].includes(page.status)

      case PageStatusFilterIds.CLOSED:
        return [
          PageStatus.FINISHED.toString(),
          PageStatus.WAITING_ISSUE.toString(),
          PageStatus.PROCESSING_ISSUE.toString(),
          PageStatus.PARTIALLY_ISSUED.toString(),
        ].includes(page.status)

      default:
        return true
    }
  })

  const cagesAddressesLabel = (
    <>
      Endereços
      <Tooltip title='Endereços das gaiolas contidas na página de transferência com NOTA EMITIDA pelo financeiro' placement='top' arrow>
        <Info sx={{
          cursor: 'pointer', marginLeft: '4px', width: '16px', height: '16px',
        }}
        />
      </Tooltip>
    </>
  )

  const tableColumns = [
    {
      id: 'cagesAddresses', label: cagesAddressesLabel, testId: 'cages-addresses-column-test-id', useSorting: false,
    },
    {
      id: 'createdAt', label: 'Criada em', testId: 'created-at-column-test-id', isDate: true, dateFormat: 'DD/MM/YYYY',
    },
    { id: 'totalSkus', label: 'Qtd SKUs', testId: 'total-skus-column-test-id' },
    {
      id: 'status', label: 'Status', testId: 'status-column-test-id',
    },
    {
      id: 'issuedNfesVsTotal', label: 'NFes emitidas / Total', testId: 'issued-nfes-vs-total-column-test-id', useSorting: false,
    },
    {
      id: 'startedAt', label: 'Data de início', testId: 'started-at-column-test-id', isDate: true,
    },
    {
      id: 'finishedAt', label: 'Data de finalização', testId: 'finished-at-column-test-id', isDate: true,
    },
    { id: 'userStarted', label: 'Keeper Responsável', testId: 'user-started-column-test-id' },
    {
      id: 'open', label: 'Abrir', testId: 'open-column-test-id', useSorting: false,
    },
    {
      id: 'remove', label: 'Remover', testId: 'remove-column-test-id', useSorting: false,
    },
  ]

  const cagesAddressesCell = (cagesAddresses: string[]) => {
    if (cagesAddresses.length === 1) {
      return (
        <Typography
          sx={{ fontSize: '14px' }}
          onClick={() => {
            navigator.clipboard.writeText(cagesAddresses[0])
            toast.info(`Endereço copiado para a área de transferência: ${cagesAddresses[0]}`)
          }}
        >{cagesAddresses[0]}
        </Typography>
      )
    }
    if (cagesAddresses.length > 1) {
      const addressesAux = cagesAddresses.join(', ')
      return (
        <Tooltip
          title={(
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ marginBottom: '.4rem' }}>Clique no endereço para copiar todos os endereços!</span>
              <span>Endereços:</span>
              {cagesAddresses.map((address, idx) => <span key={idx}>{address}</span>)}
            </Box>
        )}
          placement='top'
          arrow
        >
          <Typography
            sx={{ fontSize: '14px' }}
            onClick={() => {
              navigator.clipboard.writeText(addressesAux)
              toast.info(`Endereços copiados para a área de transferência: ${addressesAux}`)
            }}
          >
            {cagesAddresses[0]}...
          </Typography>
        </Tooltip>
      )
    }
    return '-'
  }

  const resolveTableRows = useMemo(() => pagesFilteredByStatus.map((page) => ({
    id: page.id,
    cagesAddresses: cagesAddressesCell(page.cagesAddresses),
    createdAt: page.createdAt,
    totalSkus: page.totalSkus,
    status: capitalizeString(page.status),
    issuedNfesVsTotal: page.issuedNfesVsTotal,
    startedAt: page.startedAt,
    finishedAt: page.finishedAt,
    userStarted: capitalizeString(page.userStarted ?? '-'),
    open: (
      <OpenInNew
        sx={{ cursor: 'pointer' }}
        onClick={(event) => {
          event.stopPropagation()
          handleOpenPage(page.id)
        }}
        data-testid='open-page-button-test-id'
      />
    ),
    remove: (
      <DeleteOutline
        sx={{ color: 'red', cursor: 'pointer' }}
        onClick={(event) => {
          event.stopPropagation()
          handleRemovePage(page.id)
        }}
        data-testid='remove-page-button-test-id'
      />
    ),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [pagesFilteredByStatus])

  const tableErrorsLogColumns = [
    {
      id: 'idPage', label: 'ID da Página', testId: 'id-page-column-test-id', useSorting: false,
    },
    {
      id: 'message', label: 'Mensagem', testId: 'message-column-test-id', useSorting: false,
    },
  ]

  const resolveTableRowsErrorsLog = useMemo(() => errorsLog.map((error, index) => ({
    id: index,
    idPage: error.id,
    message: error.message,
  })), [errorsLog])

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const selectedIds = resolveTableRows.map((row) => row.id)
      setSelectedListPages(selectedIds)
      return
    }
    setSelectedListPages([])
  }

  const handleCheckboxClick = (id: number) => {
    const selectedIndex = selectedListPages.findIndex((selectedListPageId) => selectedListPageId === id)

    if (selectedIndex === -1) {
      const pageFilteredByStatus = pagesFilteredByStatus.find((page) => page.id === id)

      if (pageFilteredByStatus) {
        setSelectedListPages([...selectedListPages, pageFilteredByStatus.id])
      }
    } else {
      const updatedSelectedIds = selectedListPages.filter((selectedListPageId) => selectedListPageId !== id)
      setSelectedListPages(updatedSelectedIds)
    }
  }

  const handleDownloadInvoices = () => {
    if (selectedListPages.length === 0 || preventDoubleClick) {
      return
    }

    setPreventDoubleClick(true)
    setErrorsLog([])

    const onSuccess = (errors: TableErrorsLog[]) => {
      setPreventDoubleClick(false)
      setErrorsLog(errors)
      if (errors.length === 0) {
        toast.success('Notas baixadas com sucesso!')
      } else {
        toast.info('Algumas notas apresentaram erros ao baixar. Confira o log de erros abaixo.')
      }
    }

    dispatch(actions.downloadTransferListPagesInvoices({
      idList,
      idsPages: selectedListPages,
      onError: () => setPreventDoubleClick(false),
      onSuccess,
    }))
  }

  const handleIssueInvoices = () => {
    if (selectedListPages.length === 0 || preventDoubleClick) {
      return
    }
    if (!showModalIssueInvoices) {
      setShowModalIssueInvoices(true)
    } else {
      setPreventDoubleClick(true)
      setErrorsLog([])

      const onError = () => {
        setPreventDoubleClick(false)
      }

      const onSuccess = (errors: TableErrorsLog[]) => {
        setPreventDoubleClick(false)
        setShowModalIssueInvoices(false)
        setErrorsLog(errors)
        if (errors.length === 0) {
          toast.success('Notas enviadas para emissão com sucesso!')
        } else {
          toast.info('Algumas páginas/notas apresentaram erros ao enviar para emissão. Confira o log de erros abaixo.')
        }
        getTransferListPages()
      }

      dispatch(actions.issueTransferListPagesInvoices({
        idsPages: selectedListPages,
        onError,
        onSuccess,
      }))
    }
  }

  return (
    <>
      <S.Wrapper>
        <S.CustomSelect
          formControlSx={{
            '& .MuiInputLabel-outlined': {
              transform: 'translate(14px, 13px) scale(1) !important',
            },
            '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75) !important',
            },
          }}
          autoComplete='off'
          dataTestId='status-pages-select-test-id'
          id='statusPages'
          name='statusPages'
          label='Status das páginas'
          variant='outlined'
          options={filterStatusPagesOptions}
          size='medium'
          type='text'
          optionNone={false}
          fullWidth
          defaultValue={statusPages}
          onChange={(event: any) => setStatusPages(event.target.value as number)}
        />
        <TableMui
          columns={tableColumns}
          emptyLabel='Nenhuma página encontrada para essa lista de transferência'
          rows={resolveTableRows}
          rowCount={resolveTableRows.length}
          enablePagination
          rowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
          minHeight='55vh'
          maxHeight='55vh'
          hasSorting
          hasCheckbox
          selected={selectedListPages}
          selectedCount={selectedListPages.length}
          onRowCheckboxClick={handleCheckboxClick}
          onSelectAllClick={handleSelectAllClick}
          rowsPerPageLimit={10}
          testId='transfer-list-pages-table-test-id'
        />
        <S.ContainerButtons>
          <S.CustomButton
            title='Baixar Notas'
            type='button'
            onClick={handleDownloadInvoices}
            bgColor='blue'
            hoverColor='darkBlueHover'
            letterColor='white'
            testId='download-pages-invoices-button-test-id'
            disabled={selectedListPages.length === 0 || preventDoubleClick}
          />
          <S.CustomButton
            title='Emitir Notas'
            type='button'
            onClick={handleIssueInvoices}
            bgColor='confirm'
            hoverColor='confirmHover'
            letterColor='white'
            testId='issue-pages-invoices-button-test-id'
            disabled={selectedListPages.length === 0 || preventDoubleClick}
          />
        </S.ContainerButtons>
        <Typography
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
          }}
          variant='h6'
          fontWeight='bold'
        >
          Log de Erros
        </Typography>
        <TableMui
          columns={tableErrorsLogColumns}
          emptyLabel='Nenhum erro a ser exibido'
          rows={resolveTableRowsErrorsLog}
          rowCount={resolveTableRowsErrorsLog.length}
          enablePagination
          rowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
          rowsPerPageLimit={10}
          minHeight='54vh'
          maxHeight='54vh'
          testId='errors-log-table-test-id'
        />
      </S.Wrapper>
      <ConfirmOrRejectModal
        show={modalRemoveTransferListPage.show}
        title='Remover página'
        description='Deseja realmente excluir ou cancelar a página da lista de transferência?'
        onConfirm={handleRemovePage}
        onClose={() => setModalRemoveTransferListPage({ show: false, idPage: -1 })}
        onReject={() => setModalRemoveTransferListPage({ show: false, idPage: -1 })}
        dataTestId='modal-remove-transfer-list-page-test-id'
      />
      <ConfirmOrRejectModal
        show={showModalIssueInvoices}
        title='Emitir notas'
        description='Deseja realmente emitir notas para as páginas selecionadas?'
        onConfirm={handleIssueInvoices}
        onClose={() => setShowModalIssueInvoices(false)}
        onReject={() => setShowModalIssueInvoices(false)}
        dataTestId='modal-issue-pages-invoices-test-id'
      />
    </>
  )
}

export default TransferPagesManagementTable
